import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";
import { managementActions } from "../../action-creators";
import { getDynamicsMappingFieldsThunk } from "../dynamicsApi";
import type { ExportSettingsSourceType } from "../../type";
import type { DynamicsPreDefinedType } from "../../types/shared";
import { selectCacheToken } from "../../selectors/common";

export const getDynamicsExclusionStatusThunk =
    () => async (dispatch: ThunkDispatchCommon, _getState: ThunkGetState, deps: ThunkDependencies) => {
        try {
            dispatch(managementActions.dynamics.getIntegrationLeadFilterStatusAsync.request());

            const { value } = await deps.si.api.management.getDynamicsExclusionStatus();

            dispatch(managementActions.dynamics.getIntegrationLeadFilterStatusAsync.success(value));
        } catch (error) {
            dispatch(managementActions.dynamics.getIntegrationLeadFilterStatusAsync.failure(error));
        }
    };

export const putDynamicsStatusFieldThunk =
    (externalField: string) =>
    async (dispatch: ThunkDispatchCommon, _getState: ThunkGetState, deps: ThunkDependencies) => {
        try {
            dispatch(managementActions.dynamics.putStatusFieldAsync.request());

            const { value } = await deps.si.api.management.putDynamicsStatusFields(externalField);

            if (value) {
                await dispatch(getDynamicsMappingFieldsThunk());
                dispatch(managementActions.dynamics.putStatusFieldAsync.success());
            } else {
                dispatch(managementActions.dynamics.putStatusFieldAsync.failure("Can't update Dynamics status"));
            }
        } catch (error) {
            dispatch(managementActions.dynamics.putStatusFieldAsync.failure(error));
        }
    };

export const putDynamicsExcludeThunk =
    (payload?: boolean) => async (dispatch: ThunkDispatchCommon, _getState: ThunkGetState, deps: ThunkDependencies) => {
        try {
            dispatch(managementActions.dynamics.putIntegrationLeadFilterStatusAsync.request());

            const {
                crmConnectionStatus: { isLeadsFilterEnabled },
            } = await deps.si.api.management.putDynamicsExcludeStatus(payload);

            dispatch(managementActions.dynamics.putIntegrationLeadFilterStatusAsync.success(isLeadsFilterEnabled));
        } catch (error) {
            dispatch(managementActions.dynamics.putIntegrationLeadFilterStatusAsync.failure(error));
        }
    };

export const getDynamicsExcludeStatusFieldsThunk =
    ({
        columnName,
        isPreDefined = false,
        type,
        sourceType,
    }: {
        columnName: string;
        isPreDefined: boolean;
        type: DynamicsPreDefinedType;
        sourceType: ExportSettingsSourceType;
    }) =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState, deps: ThunkDependencies) => {
        try {
            dispatch(managementActions.dynamics.getExcludeStatusFieldsAsync.request());
            const cacheToken = selectCacheToken(getState());

            let response = undefined;

            if (isPreDefined) {
                response = await deps.si.api.management.getDynamicsPreDefinedColumns(type, cacheToken);
            } else {
                response = await deps.si.api.management.getDynamicsFields(columnName, type, cacheToken, sourceType);
            }

            dispatch(managementActions.dynamics.getExcludeStatusFieldsAsync.success(response));
        } catch (error) {
            dispatch(managementActions.dynamics.getDynamicsStatusAsync.failure(error));
        }
    };

export const getDynamicsExcludeAccountFieldsThunk =
    () => async (dispatch: ThunkDispatchCommon, _getState: ThunkGetState, deps: ThunkDependencies) => {
        try {
            dispatch(managementActions.dynamics.getExcludeAccountFieldsAsync.request());

            const response = await deps.si.api.management.getDynamicsExcludeAccountFields();
            dispatch(managementActions.dynamics.getExcludeAccountFieldsAsync.success(response));
        } catch (error) {
            dispatch(managementActions.dynamics.getExcludeAccountFieldsAsync.failure(error));
        }
    };
